import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Edit as EditIcon, Description as ReportsIcon, Block as BlockIcon, CheckCircle as EnableIcon } from '@mui/icons-material';
import axios from 'axios';
import config from '../config';

function AgentList() {
  const [agents, setAgents] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        console.log('Fetching agents...');
        const response = await axios.get(`${config.apiBaseUrl}/api/agents`);
        console.log('Agents response:', response.data);
        
        if (!Array.isArray(response.data)) {
          throw new Error('Invalid response format');
        }
        
        setAgents(response.data);
      } catch (err) {
        console.error('Error fetching agents:', err);
        console.error('Error response:', err.response?.data);
        
        if (err.response?.status === 401) {
          navigate('/login');
        } else {
          setError(err.response?.data?.message || 'Failed to fetch agents');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, [navigate]);

  const handleViewReports = (customAgentId) => {
    console.log('Viewing reports for agent:', customAgentId);
    navigate(`/agents/${customAgentId}/call-reports`);
  };

  const handleEditAgent = (agentId) => {
    navigate(`/agents/${agentId}/edit`);
  };

  const handleToggleAgentStatus = async (agentId, agentName, isCurrentlyDisabled) => {
    try {
      const action = isCurrentlyDisabled ? 'enable' : 'disable';
      const confirmed = window.confirm(`Are you sure you want to ${action} login for agent "${agentName}"?`);
      if (!confirmed) return;

      console.log(`${action}ing agent:`, agentId);
      const endpoint = isCurrentlyDisabled ? 'enable' : 'disable';
      const response = await axios.patch(`${config.apiBaseUrl}/api/agents/${agentId}/${endpoint}`, {}, {
        withCredentials: true
      });
      
      console.log(`Agent ${action}d successfully:`, response.data);
      
      // Refresh the agents list
      const updatedResponse = await axios.get(`${config.apiBaseUrl}/api/agents`);
      setAgents(updatedResponse.data);
      
    } catch (err) {
      console.error(`Error ${isCurrentlyDisabled ? 'enabling' : 'disabling'} agent:`, err);
      setError(err.response?.data?.message || `Failed to ${isCurrentlyDisabled ? 'enable' : 'disable'} agent`);
    }
  };

  const handleCreateNew = () => {
    navigate('/agents/new');
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100vh', p: 3 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Agents
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateNew}
          >
            Create New Agent
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {agents.length === 0 ? (
        <Alert severity="info">No agents found.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Agent ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent._id}>
                  <TableCell>{agent.customAgentId}</TableCell>
                  <TableCell>{agent.name}</TableCell>
                  <TableCell>{agent.email}</TableCell>
                  <TableCell>{agent.phone}</TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={agent.isDisabled ? 'error.main' : 'success.main'}
                    >
                      {agent.isDisabled ? 'Disabled' : 'Active'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleViewReports(agent.customAgentId)}
                      title="View Reports"
                    >
                      <ReportsIcon />
                    </IconButton>
                    {isAdmin && (
                      <>
                        <Tooltip title="Edit Agent">
                          <IconButton
                            onClick={() => handleEditAgent(agent._id)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={agent.isDisabled ? "Enable Agent Login" : "Disable Agent Login"}>
                          <IconButton
                            onClick={() => handleToggleAgentStatus(agent._id, agent.name, agent.isDisabled)}
                            color={agent.isDisabled ? "success" : "error"}
                          >
                            {agent.isDisabled ? <EnableIcon /> : <BlockIcon />}
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default AgentList; 